import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import minorMemberService from '../../services/minorMemberService';
import memberService from '../../services/memberService';
import { addNotification } from '../../store/slices/uiSlice';
import { getErrorMessage } from '../../utils/errorMessage';
import './MinorMemberForm.css';

const EMPTY_FORM = {
  worship_id: '',
  member_type: 'children',
  status_name: 'active',
  chi_name: '',
  eng_name: '',
  sex_name: '',
  date_of_birth: '',
  edu_grade: '',
  school_name: '',
  tel_home: '',
  mobile: '',
  fax: '',
  email: '',
  region: '',
  street: '',
  building: '',
  postal_region: '',
  postal_street: '',
  postal_building: '',
  emergency_contact_name: '',
  emergency_contact_tel: '',
  emergency_contact_relation: '',
  needs_medication: false,
  medication_reason: '',
  has_allergy: false,
  allergy_detail: '',
  can_exercise: true,
  health_notes: '',
  districts: '',
  groups: '',
  photo_consent: true,
  data_use_consent: true,
  date_of_join: new Date().toISOString().split('T')[0],
  remark: '',
};

const EMPTY_FAMILY = { relation: '父親', chi_name: '', is_church_member: false, worship_code: '' };
const FAMILY_RELATIONS = ['父親', '母親', '兄', '弟', '姊', '妹', '監護人'];

const FAMILY_RELATION_GROUPS = [
  { label: '祖父母輩', options: ['祖父', '祖母', '外祖父', '外祖母'] },
  { label: '父母輩',   options: ['父親', '母親', '繼父', '繼母', '養父', '養母', '監護人',
                                  '伯父', '伯母', '叔父', '叔母', '舅父', '舅母', '姑父', '姑母', '姨父', '姨母'] },
  { label: '同輩',     options: ['兄', '弟', '姊', '妹', '堂兄', '堂弟', '堂姊', '堂妹', '表兄', '表弟', '表姊', '表妹'] },
];

const TYPE_LABEL = { regular: '成人', associate: '成人', temporary: '成人', children: '兒童', teenage: '少年' };
const TYPE_COLOR = { regular: '#059669', associate: '#059669', temporary: '#059669', children: '#0ea5e9', teenage: '#7c3aed' };

// ── Autocomplete row for a single family member ───────────────────────────────
const FamilyMemberRow = ({ f, idx, onUpdate, onRemove }) => {
  const [query, setQuery]           = useState(f.worship_code || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showDrop, setShowDrop]     = useState(false);
  const [searching, setSearching]   = useState(false);
  const debounceRef = useRef(null);
  const blurTimerRef = useRef(null);

  // Keep query in sync if parent resets worship_code externally
  useEffect(() => {
    if (!f.is_church_member) setQuery('');
  }, [f.is_church_member]);

  const runSearch = useCallback(async (q) => {
    if (!q.trim()) { setSuggestions([]); setShowDrop(false); return; }
    setSearching(true);
    try {
      // Search adults (6-digit) and minors (4-digit) in parallel
      const [adults, minors] = await Promise.all([
        memberService.searchMembers(q, 8),
        minorMemberService.searchMembers(q, 8),
      ]);
      const adultItems = (adults || []).map(m => ({
        worship_code: m.worship_id,
        chi_name: m.chi_name,
        member_type: 'adult',
        label: `${m.worship_id} · ${m.chi_name}`,
      }));
      const minorItems = (minors || []).map(m => ({
        worship_code: m.worship_id,
        chi_name: m.chi_name,
        member_type: m.member_type,
        label: `${m.worship_id} · ${m.chi_name}`,
      }));
      setSuggestions([...adultItems, ...minorItems]);
      setShowDrop(true);
    } catch { setSuggestions([]); }
    finally { setSearching(false); }
  }, []);

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onUpdate(idx, 'worship_code', val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(val), 300);
  };

  const handleSelect = (item) => {
    setQuery(item.worship_code);
    setSuggestions([]);
    setShowDrop(false);
    onUpdate(idx, 'worship_code', item.worship_code);
    onUpdate(idx, 'chi_name', item.chi_name || '');
  };

  const handleFocus = () => {
    clearTimeout(blurTimerRef.current);
    if (suggestions.length > 0) setShowDrop(true);
  };

  const handleBlur = () => {
    blurTimerRef.current = setTimeout(() => setShowDrop(false), 200);
  };

  return (
    <tr key={idx}>
      <td>
        <select
          value={f.relation}
          onChange={e => onUpdate(idx, 'relation', e.target.value)}
          className="form-control form-control-sm"
        >
          {FAMILY_RELATION_GROUPS.map(g => (
            <optgroup key={g.label} label={g.label}>
              {g.options.map(r => <option key={r} value={r}>{r}</option>)}
            </optgroup>
          ))}
        </select>
      </td>
      <td>
        <input
          type="text"
          value={f.chi_name || ''}
          onChange={e => onUpdate(idx, 'chi_name', e.target.value)}
          className="form-control form-control-sm"
          placeholder="姓名"
        />
      </td>
      <td className="text-center">
        <input
          type="checkbox"
          checked={f.is_church_member}
          onChange={e => {
            onUpdate(idx, 'is_church_member', e.target.checked);
            if (!e.target.checked) {
              setQuery('');
              setSuggestions([]);
              onUpdate(idx, 'worship_code', '');
            }
          }}
        />
      </td>
      <td style={{ position: 'relative', minWidth: '160px' }}>
        {f.is_church_member && (
          <>
            <input
              type="text"
              value={query}
              onChange={handleQueryChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              className="form-control form-control-sm family-worship-input"
              placeholder="搜尋姓名 / 敬拜號"
              autoComplete="off"
            />
            {searching && <div className="fam-searching">搜尋中...</div>}
            {showDrop && suggestions.length > 0 && (
              <ul className="fam-suggestions">
                {suggestions.map((s, si) => (
                  <li
                    key={si}
                    className="fam-suggestion-item"
                    onMouseDown={e => { e.preventDefault(); handleSelect(s); }}
                  >
                    <span
                      className="fam-type-badge"
                      style={{ background: TYPE_COLOR[s.member_type] || '#6b7280' }}
                    >
                      {TYPE_LABEL[s.member_type] || s.member_type}
                    </span>
                    <span className="fam-code">{s.worship_code}</span>
                    <span className="fam-name">{s.chi_name}</span>
                  </li>
                ))}
              </ul>
            )}
            {showDrop && !searching && suggestions.length === 0 && query.trim() && (
              <div className="fam-no-results">找不到相關會員</div>
            )}
          </>
        )}
      </td>
      <td>
        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => onRemove(idx)}>
          <i className="fas fa-times"></i>
        </button>
      </td>
    </tr>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const EDU_GRADES = {
  children: ['幼稚園低班', '幼稚園高班', '小一', '小二', '小三', '小四', '小五', '小六'],
  teenage:  ['中一', '中二', '中三', '中四', '中五', '中六'],
  all:      ['幼稚園低班', '幼稚園高班', '小一', '小二', '小三', '小四', '小五', '小六',
             '中一', '中二', '中三', '中四', '中五', '中六'],
};

const MinorMemberForm = ({ mode = 'create', fixedType = null }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = mode === 'edit' || !!id;

  const defaultType = fixedType || 'children';

  const [formData, setFormData] = useState({ ...EMPTY_FORM, member_type: defaultType });
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [suggestedId, setSuggestedId] = useState('');

  // Load data for edit
  useEffect(() => {
    if (isEdit && id) {
      loadMember();
    } else {
      // Auto-suggest next worship ID for create
      loadNextId(defaultType);
    }
  }, [id, isEdit]);

  const loadMember = async () => {
    setLoading(true);
    try {
      const m = await minorMemberService.getById(id);
      setFormData({
        worship_id: m.worship_id || '',
        member_type: m.member_type || 'children',
        status_name: m.status_name || 'active',
        chi_name: m.chi_name || '',
        eng_name: m.eng_name || '',
        sex_name: m.sex_name || '',
        date_of_birth: m.date_of_birth ? new Date(m.date_of_birth).toISOString().split('T')[0] : '',
        edu_grade: m.edu_grade || '',
        school_name: m.school_name || '',
        tel_home: m.tel_home || '',
        mobile: m.mobile || '',
        fax: m.fax || '',
        email: m.email || '',
        region: m.region || '',
        street: m.street || '',
        building: m.building || '',
        postal_region: m.postal_region || '',
        postal_street: m.postal_street || '',
        postal_building: m.postal_building || '',
        emergency_contact_name: m.emergency_contact_name || '',
        emergency_contact_tel: m.emergency_contact_tel || '',
        emergency_contact_relation: m.emergency_contact_relation || '',
        needs_medication: m.needs_medication || false,
        medication_reason: m.medication_reason || '',
        has_allergy: m.has_allergy || false,
        allergy_detail: m.allergy_detail || '',
        can_exercise: m.can_exercise !== undefined ? m.can_exercise : true,
        health_notes: m.health_notes || '',
        districts: m.districts || '',
        groups: m.groups || '',
        photo_consent: m.photo_consent !== undefined ? m.photo_consent : true,
        data_use_consent: m.data_use_consent !== undefined ? m.data_use_consent : true,
        date_of_join: m.date_of_join ? new Date(m.date_of_join).toISOString().split('T')[0] : '',
        remark: m.remark || '',
      });
      setFamilyMembers(m.family_members || []);
    } catch (err) {
      dispatch(addNotification({ type: 'error', title: '錯誤', message: '無法載入會員資料' }));
      navigate('/minor-members');
    } finally {
      setLoading(false);
    }
  };

  const loadNextId = async (type) => {
    try {
      const res = await minorMemberService.getNextWorshipId(type);
      setSuggestedId(res.next_worship_id);
      if (!isEdit) {
        setFormData(prev => ({ ...prev, worship_id: res.next_worship_id }));
      }
    } catch (err) {
      console.warn('Could not fetch next worship ID', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // Family member helpers
  const addFamilyMember = () => setFamilyMembers(prev => [...prev, { ...EMPTY_FAMILY }]);

  const updateFamilyMember = (idx, field, value) => {
    setFamilyMembers(prev => prev.map((f, i) => i === idx ? { ...f, [field]: value } : f));
  };

  const removeFamilyMember = (idx) => {
    setFamilyMembers(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.worship_id || !/^\d{4}$/.test(formData.worship_id)) {
      dispatch(addNotification({ type: 'error', title: '驗證錯誤', message: '敬拜號必須是4位數字' }));
      setActiveTab('personal');
      return;
    }
    if (!formData.chi_name.trim()) {
      dispatch(addNotification({ type: 'error', title: '驗證錯誤', message: '請填寫中文姓名' }));
      setActiveTab('personal');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        family_members: familyMembers.length > 0 ? familyMembers : null,
      };
      // Clean empty strings to null for optional fields
      Object.keys(payload).forEach(k => {
        if (payload[k] === '') payload[k] = null;
      });
      // worship_id and chi_name must remain as strings
      payload.worship_id = formData.worship_id;
      payload.chi_name = formData.chi_name;
      // Convert date-only strings (YYYY-MM-DD) to ISO datetime (Pydantic v2 requirement)
      const DATE_FIELDS = ['date_of_birth', 'date_of_join'];
      DATE_FIELDS.forEach(f => {
        if (payload[f] && /^\d{4}-\d{2}-\d{2}$/.test(payload[f])) {
          payload[f] = `${payload[f]}T00:00:00`;
        }
      });

      if (isEdit) {
        await minorMemberService.update(id, payload);
        dispatch(addNotification({ type: 'success', title: '成功', message: '會員資料已更新' }));
        navigate(`/minor-members/${id}`);
      } else {
        const created = await minorMemberService.create(payload);
        dispatch(addNotification({ type: 'success', title: '成功', message: `會員已新增，敬拜號: ${created.worship_id}` }));
        navigate(`/minor-members/${created.id}`);
      }
    } catch (err) {
      const msg = getErrorMessage(err, isEdit ? '更新失敗' : '新增失敗');
      dispatch(addNotification({ type: 'error', title: '錯誤', message: msg }));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner-border" role="status" />
        <p>載入中...</p>
      </div>
    );
  }

  const tabs = [
    { id: 'personal', label: '個人資料', icon: 'fas fa-user' },
    { id: 'contact', label: '聯絡資料', icon: 'fas fa-phone' },
    { id: 'address', label: '地址', icon: 'fas fa-home' },
    { id: 'health', label: '健康', icon: 'fas fa-heartbeat' },
    { id: 'family', label: '家庭狀況', icon: 'fas fa-users' },
    { id: 'ministry', label: '事奉/備註', icon: 'fas fa-church' },
  ];

  return (
    <div className="minor-member-form-container">
      <div className="form-header">
        <h2>
          <i className={`fas ${isEdit ? 'fa-edit' : 'fa-user-plus'} mr-2`}></i>
          {isEdit
            ? '編輯少年兒童會員'
            : fixedType === 'children' ? '新增兒童會員'
            : fixedType === 'teenage'  ? '新增少年會員'
            : '新增少年兒童會員'
          }
        </h2>
        <button className="btn btn-secondary" onClick={() => navigate('/minor-members')}>
          <i className="fas fa-arrow-left"></i> 返回列表
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Tab navigation */}
        <div className="form-tabs">
          {tabs.map(t => (
            <button
              key={t.id}
              type="button"
              className={`form-tab ${activeTab === t.id ? 'active' : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              <i className={t.icon}></i> {t.label}
            </button>
          ))}
        </div>

        <div className="form-body">

          {/* ── Personal ─────────────────────────────────── */}
          {activeTab === 'personal' && (
            <div className="form-section">
              <h3 className="section-title">個人資料</h3>

              <div className="form-row">
                <div className="form-group col-md-3">
                  <label className="required">類別</label>
                  {fixedType ? (
                    <div className="form-control-static">
                      <span className={`badge badge-lg ${fixedType === 'children' ? 'badge-info' : 'badge-purple'}`}>
                        {fixedType === 'children' ? '兒童' : '少年'}
                      </span>
                    </div>
                  ) : (
                    <select name="member_type" value={formData.member_type} onChange={handleChange} className="form-control">
                      <option value="children">兒童</option>
                      <option value="teenage">少年</option>
                    </select>
                  )}
                </div>
                <div className="form-group col-md-3">
                  <label className="required">敬拜號 (4位數字)</label>
                  <input
                    type="text"
                    name="worship_id"
                    value={formData.worship_id}
                    onChange={handleChange}
                    className="form-control worship-id-input"
                    maxLength={4}
                    pattern="\d{4}"
                    placeholder={suggestedId || '0001'}
                    required
                  />
                  {suggestedId && !isEdit && (
                    <small className="text-muted">建議: {suggestedId}</small>
                  )}
                </div>
                <div className="form-group col-md-3">
                  <label className="required">狀態</label>
                  <select name="status_name" value={formData.status_name} onChange={handleChange} className="form-control">
                    <option value="active">在籍</option>
                    <option value="inactive">停籍</option>
                    <option value="pending">待審核 (Pending)</option>
                  </select>
                </div>
                <div className="form-group col-md-3">
                  <label className="required">入會日期</label>
                  <input type="date" name="date_of_join" value={formData.date_of_join} onChange={handleChange} className="form-control" />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-md-4">
                  <label className="required">中文姓名</label>
                  <input type="text" name="chi_name" value={formData.chi_name} onChange={handleChange} className="form-control" required />
                </div>
                <div className="form-group col-md-4">
                  <label>英文姓名</label>
                  <input type="text" name="eng_name" value={formData.eng_name} onChange={handleChange} className="form-control" />
                </div>
                <div className="form-group col-md-2">
                  <label>性別</label>
                  <select name="sex_name" value={formData.sex_name} onChange={handleChange} className="form-control">
                    <option value="">—</option>
                    <option value="男">男</option>
                    <option value="女">女</option>
                  </select>
                </div>
                <div className="form-group col-md-2">
                  <label>出生日期</label>
                  <input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} className="form-control" />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-md-4">
                  <label>就讀級別</label>
                  <select name="edu_grade" value={formData.edu_grade} onChange={handleChange} className="form-control">
                    <option value="">—</option>
                    {(EDU_GRADES[fixedType || formData.member_type] || EDU_GRADES.all).map(g => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group col-md-8">
                  <label>就讀學校</label>
                  <input type="text" name="school_name" value={formData.school_name} onChange={handleChange} className="form-control" />
                </div>
              </div>
            </div>
          )}

          {/* ── Contact ───────────────────────────────────── */}
          {activeTab === 'contact' && (
            <div className="form-section">
              <h3 className="section-title">聯絡資料</h3>
              <div className="form-row">
                <div className="form-group col-md-4">
                  <label>手提電話</label>
                  <input type="tel" name="mobile" value={formData.mobile} onChange={handleChange} className="form-control" />
                </div>
                <div className="form-group col-md-4">
                  <label>住宅電話</label>
                  <input type="tel" name="tel_home" value={formData.tel_home} onChange={handleChange} className="form-control" />
                </div>
                <div className="form-group col-md-4">
                  <label>傳真</label>
                  <input type="tel" name="fax" value={formData.fax} onChange={handleChange} className="form-control" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label>電郵</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} className="form-control" />
                </div>
              </div>

              <h3 className="section-title mt-4">緊急聯絡人</h3>
              <div className="form-row">
                <div className="form-group col-md-4">
                  <label>姓名</label>
                  <input type="text" name="emergency_contact_name" value={formData.emergency_contact_name} onChange={handleChange} className="form-control" />
                </div>
                <div className="form-group col-md-4">
                  <label>電話</label>
                  <input type="tel" name="emergency_contact_tel" value={formData.emergency_contact_tel} onChange={handleChange} className="form-control" />
                </div>
                <div className="form-group col-md-4">
                  <label>關係</label>
                  <input type="text" name="emergency_contact_relation" value={formData.emergency_contact_relation} onChange={handleChange} className="form-control" placeholder="例: 父親" />
                </div>
              </div>
            </div>
          )}

          {/* ── Address ───────────────────────────────────── */}
          {activeTab === 'address' && (
            <div className="form-section">
              <h3 className="section-title">住宅地址</h3>
              <div className="form-row">
                <div className="form-group col-md-4">
                  <label>地區</label>
                  <input type="text" name="region" value={formData.region} onChange={handleChange} className="form-control" placeholder="例: 九龍城" />
                </div>
                <div className="form-group col-md-4">
                  <label>街道</label>
                  <input type="text" name="street" value={formData.street} onChange={handleChange} className="form-control" />
                </div>
                <div className="form-group col-md-4">
                  <label>大廈/屋苑</label>
                  <input type="text" name="building" value={formData.building} onChange={handleChange} className="form-control" />
                </div>
              </div>

              <h3 className="section-title mt-4">通訊地址 <small className="text-muted">(如與住宅相同可留空)</small></h3>
              <div className="form-row">
                <div className="form-group col-md-4">
                  <label>地區</label>
                  <input type="text" name="postal_region" value={formData.postal_region} onChange={handleChange} className="form-control" />
                </div>
                <div className="form-group col-md-4">
                  <label>街道</label>
                  <input type="text" name="postal_street" value={formData.postal_street} onChange={handleChange} className="form-control" />
                </div>
                <div className="form-group col-md-4">
                  <label>大廈/屋苑</label>
                  <input type="text" name="postal_building" value={formData.postal_building} onChange={handleChange} className="form-control" />
                </div>
              </div>
            </div>
          )}

          {/* ── Health ───────────────────────────────────── */}
          {activeTab === 'health' && (
            <div className="form-section">
              <h3 className="section-title">健康狀況</h3>

              <div className="health-question">
                <label className="health-check-label">
                  <input type="checkbox" name="needs_medication" checked={formData.needs_medication} onChange={handleChange} />
                  <span>是否需要定時服藥？</span>
                </label>
                {formData.needs_medication && (
                  <div className="form-group mt-2">
                    <label>藥物詳情</label>
                    <input type="text" name="medication_reason" value={formData.medication_reason} onChange={handleChange} className="form-control" placeholder="請說明藥物名稱及服藥時間" />
                  </div>
                )}
              </div>

              <div className="health-question">
                <label className="health-check-label">
                  <input type="checkbox" name="has_allergy" checked={formData.has_allergy} onChange={handleChange} />
                  <span>是否有過敏症？</span>
                </label>
                {formData.has_allergy && (
                  <div className="form-group mt-2">
                    <label>過敏詳情</label>
                    <input type="text" name="allergy_detail" value={formData.allergy_detail} onChange={handleChange} className="form-control" placeholder="請說明過敏原" />
                  </div>
                )}
              </div>

              <div className="health-question">
                <label className="health-check-label">
                  <input type="checkbox" name="can_exercise" checked={formData.can_exercise} onChange={handleChange} />
                  <span>可以參加一般體力活動</span>
                </label>
              </div>

              <div className="form-group mt-3">
                <label>其他健康備註</label>
                <textarea name="health_notes" value={formData.health_notes} onChange={handleChange} className="form-control" rows={3} />
              </div>
            </div>
          )}

          {/* ── Family ───────────────────────────────────── */}
          {activeTab === 'family' && (
            <div className="form-section">
              <div className="section-header-row">
                <h3 className="section-title">家庭狀況</h3>
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={addFamilyMember}>
                  <i className="fas fa-plus"></i> 新增成員
                </button>
              </div>

              {familyMembers.length === 0 ? (
                <p className="text-muted">尚未新增家庭成員。</p>
              ) : (
                <table className="table table-sm family-table">
                  <thead>
                    <tr>
                      <th>關係</th>
                      <th>中文姓名</th>
                      <th>本教會會友</th>
                      <th>敬拜號</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {familyMembers.map((f, idx) => (
                      <FamilyMemberRow
                        key={idx}
                        f={f}
                        idx={idx}
                        onUpdate={updateFamilyMember}
                        onRemove={removeFamilyMember}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ── Ministry / Remarks ───────────────────────── */}
          {activeTab === 'ministry' && (
            <div className="form-section">
              <h3 className="section-title">事奉分配</h3>
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label>區</label>
                  <input type="text" name="districts" value={formData.districts} onChange={handleChange} className="form-control" />
                </div>
                <div className="form-group col-md-6">
                  <label>組</label>
                  <input type="text" name="groups" value={formData.groups} onChange={handleChange} className="form-control" />
                </div>
              </div>

              <h3 className="section-title mt-4">同意聲明</h3>
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="health-check-label">
                    <input type="checkbox" name="photo_consent" checked={formData.photo_consent} onChange={handleChange} />
                    <span>同意拍攝及使用相片</span>
                  </label>
                </div>
                <div className="form-group col-md-6">
                  <label className="health-check-label">
                    <input type="checkbox" name="data_use_consent" checked={formData.data_use_consent} onChange={handleChange} />
                    <span>同意使用個人資料</span>
                  </label>
                </div>
              </div>

              <h3 className="section-title mt-4">備註</h3>
              <div className="form-group">
                <textarea name="remark" value={formData.remark} onChange={handleChange} className="form-control" rows={4} />
              </div>
            </div>
          )}
        </div>

        {/* Form actions */}
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={() => navigate(isEdit ? `/minor-members/${id}` : '/minor-members')}>
            取消
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? (
              <><span className="spinner-border spinner-border-sm mr-2"></span>儲存中...</>
            ) : (
              <><i className="fas fa-save mr-2"></i>{isEdit ? '儲存更改' : '新增會員'}</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MinorMemberForm;
